import React, { useMemo } from "react";
import { FlaskConical, ShieldAlert, Clock, Wrench } from "lucide-react";

const RISK_COLOR = { Safe: "#10B981", Caution: "#F59E0B", Unsafe: "#EF4444" };

const ZoneMap = ({ grid }) => (
  <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${grid[0].length}, 1fr)` }} data-testid="chemical-zone-map">
    {grid.flatMap((row, ri) => row.map((v, ci) => (
      <div key={`${ri}-${ci}`} title={`Anomaly ${v}`} style={{ background: `rgba(239,68,68,${0.08 + v * 0.9})`, aspectRatio: "1" }} />
    )))}
  </div>
);

export const ChemicalDetectionView = ({ pesticide }) => {
  const { compounds = [], zone_map, residue_risk, safe_harvest_days, affected_area_pct, mrl_exceedances } = pesticide;
  const exceeding = useMemo(() => compounds.filter((c) => c.exceeds_mrl), [compounds]);
  const riskColor = RISK_COLOR[residue_risk] || "#94A3B8";
  if (!zone_map) return null;

  return (
    <div className="qs-panel p-6 space-y-5" data-testid="chemical-detection-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-[#00FFFF]" />
          <h4 className="font-display font-semibold">Pesticide &amp; Chemical Waste Detection</h4>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Non-destructive · SWIR fingerprinting</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#050A15] p-3 border border-white/5" data-testid="residue-risk">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500"><ShieldAlert className="h-3 w-3" /> Residue risk</div>
          <div className="font-display font-bold text-xl mt-1" style={{ color: riskColor }}>{residue_risk}</div>
        </div>
        <div className="bg-[#050A15] p-3 border border-white/5" data-testid="safe-harvest">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500"><Clock className="h-3 w-3" /> Safe harvest</div>
          <div className="font-mono font-bold text-xl mt-1">{safe_harvest_days === 0 ? "Now" : `${safe_harvest_days}d`}</div>
        </div>
        <div className="bg-[#050A15] p-3 border border-white/5" data-testid="affected-area">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Affected area</div>
          <div className="font-mono font-bold text-xl mt-1">{affected_area_pct}%</div>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_200px] gap-5">
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="compound-table">
            <thead className="text-slate-500 uppercase tracking-wider text-[10px]">
              <tr className="border-b border-white/10">
                <th className="text-left py-2">Compound</th><th className="text-left">Band</th>
                <th className="text-right">ppm</th><th className="text-right">MRL</th><th className="text-right">Conf.</th>
              </tr>
            </thead>
            <tbody>
              {compounds.map((c) => (
                <tr key={c.compound} className="border-b border-white/5" data-testid={`compound-row-${c.compound.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td className="py-2">
                    <div className="text-slate-200">{c.compound}</div>
                    <div className="text-[10px] text-slate-500 font-sans">{c.class}</div>
                  </td>
                  <td className="text-slate-400">{c.diagnostic_band_nm}nm</td>
                  <td className="text-right font-bold" style={{ color: c.exceeds_mrl ? "#EF4444" : "#10B981" }}>{c.concentration_ppm}</td>
                  <td className="text-right text-slate-400">{c.mrl_ppm}</td>
                  <td className="text-right text-slate-400">{Math.round(c.confidence * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-slate-500 mt-2">{mrl_exceedances} MRL exceedance(s) · {pesticide.reference}</p>
        </div>
        <div>
          <ZoneMap grid={zone_map} />
          <div className="text-[10px] font-mono text-slate-500 mt-2 text-center">Contamination anomaly map</div>
        </div>
      </div>

      {exceeding.length > 0 && (
        <div className="space-y-2" data-testid="remediation-plan">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F59E0B]"><Wrench className="h-3.5 w-3.5" /> Remediation plan</div>
          {exceeding.map((c) => (
            <div key={c.compound} className="flex gap-3 text-sm border-l-2 border-[#F59E0B]/60 pl-3">
              <span className="font-mono text-slate-400 shrink-0">{c.compound}</span>
              <span className="text-slate-300">{c.remediation}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
